import clsx from 'clsx';
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import Icon from '@mui/material/Icon';

const DEFAULT_SIZE = 24;

const Root = styled(Box)(({ theme, size = DEFAULT_SIZE, color = 'inherit' }) => ({
  width: size,
  height: size,
  minWidth: size,
  minHeight: size,
  fontSize: size,
  lineHeight: size,
  color: {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    action: theme.palette.action.active,
    error: theme.palette.error.main,
    disabled: theme.palette.action.disabled,
    inherit: 'currentColor',
  }[color],
}));

function getIconName(children) {
  if (typeof children === 'string') {
    return children;
  }

  if (Array.isArray(children) && children.length === 1 && typeof children[0] === 'string') {
    return children[0];
  }

  return null;
}

const FuseSvgIcon = forwardRef((props, ref) => {
  const {
    children,
    className = '',
    color = 'inherit',
    size = DEFAULT_SIZE,
    sx,
    ...rest
  } = props;

  const iconName = getIconName(children);

  if (!iconName) {
    return null;
  }

  if (!iconName.includes(':')) {
    return (
      <Icon ref={ref} className={className} color={color} sx={sx} {...rest}>
        {iconName}
      </Icon>
    );
  }

  const iconPath = iconName.replace(':', '.svg#');
  const spriteHref = `/assets/icons/${iconPath}`;

  return (
    <Root
      {...rest}
      component="svg"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={clsx('shrink-0 fill-current', className)}
      ref={ref}
      size={size}
      sx={sx}
      color={color}
    >
      <use href={spriteHref} xlinkHref={spriteHref} />
    </Root>
  );
});

FuseSvgIcon.displayName = 'FuseSvgIcon';

FuseSvgIcon.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
  color: PropTypes.oneOf([
    'inherit',
    'disabled',
    'primary',
    'secondary',
    'action',
    'error',
    'info',
    'success',
    'warning',
  ]),
};

export default FuseSvgIcon;
